import { adjustContentVisibility, SectionTitle, SectionParagraph as Subtitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import bottomCTALottieData from '@/static/assets/homeowners/Bottom_animation.json';
import bottomCTALottieDataMobile from '@/static/assets/homeowners/Bottom_animation_mobile.json';
import { Flex, useLottie } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import { useRef } from 'react';
import styled from 'styled-components';
import { PRODUCT_HOME_US_HOMEOWNERS } from '../../utils/products';

const Title = styled(SectionTitle)`
  text-wrap: balance;
`;

const BottomCTAContainer = styled(Flex)`
  ${adjustContentVisibility}
  width: 100%;
  text-align: center;
  position: relative;
`;

const Band = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 105px;
  background-color: ${({ theme }) => theme.colors.cloud};
  z-index: -1;

  ${MEDIA_QUERIES.tabletAndDesktop`
      height: 170px;
  `}
`;

const LottieDesktop = styled(Flex)`
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: flex;
    flex-shrink: 0;
  `}
`;

const LottieMobile = styled(Flex)`
  display: flex;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}
`;

const WindowImage = styled(Image)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-500px, -50%);
  z-index: -1;
  margin-top: 22px;
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block;
  `}
`;

export interface BottomCTAProps {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  windowImage: StaticImageData;
}

const Animation: React.FC = () => {
  const lottieRefDesktop = useRef<Element>(null);
  const lottieRefMobile = useRef<Element>(null);

  useLottie(lottieRefDesktop, {
    animationData: bottomCTALottieData,
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      pauseOnScroll: false,
      playTrigger: {
        threshold: 0,
        condition: 'intersect',
        pauseIfConditionsUnmet: true,
      },
    },
  });

  useLottie(lottieRefMobile, {
    animationData: bottomCTALottieDataMobile,
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      playTrigger: {
        threshold: 0,
        condition: 'intersect',
        pauseIfConditionsUnmet: true,
      },
    },
  });
  return (
    <>
      <LottieMobile ref={lottieRefMobile} width={701} height={410} position="relative" zIndex={-1} />
      <LottieDesktop ref={lottieRefDesktop} width={1679} height={574} position="relative" zIndex={-1} />
    </>
  );
};

export const BottomCTA: React.FC<BottomCTAProps> = ({
  title = 'Prices that aren’t through the roof',
  subtitle = 'Starting from just $25/mo.',
  ctaLabel = 'Check our prices',
  ctaUrl,
  windowImage,
}) => {
  return (
    <BottomCTAContainer pt={[40, 70]} width="100%" alignItems="center" flexDirection="column" zIndex={1}>
      <Band />
      <WindowImage src={windowImage} alt="" />

      <Flex pb={46} flexDirection="column" gap="20px" px="20px">
        <Title tabIndex={0}>{parse(title)}</Title>
        <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
      </Flex>

      <JoinCTA
        size="big"
        flavor="primary"
        glowing
        data-source="homeowners"
        product={PRODUCT_HOME_US_HOMEOWNERS}
        customUrl={ctaUrl}
        minWidth={233}
        data-position="bottom"
      >
        {ctaLabel}
      </JoinCTA>
      <Animation />
    </BottomCTAContainer>
  );
};
